import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Fold } from "./Fold";
import { SectionTitle } from "./Heading";
import { addInterest, type AddedOffer } from "../lib/offerAdds";
import { setOfferPinned } from "../lib/offersPage";
import { useAddDismissals } from "./addDismissals";
import { AddedForYou, VanishedAdd } from "./AddedForYou";
import { NextRunList } from "./NextRunList";
import { OfferLine } from "./OfferLine";
import {
  addsFor,
  endsSoon,
  fetchRecommended,
  matchesFilter,
  offerKey,
  readCoverage,
  setDismissed,
  setFlagged,
  type OfferTab,
  type Recommended,
  type RecommendedIssuer,
  type RecommendedOffer,
  type WorthFilter,
} from "../lib/worthAdding";

import { invalidateOffers, useOfferRevision, useOfferMutations } from "../hooks/useOfferData";

const UNDO_MS = 5000;
const FILTERS: [WorthFilter, string][] = [
  ["all", "All"], ["brand", "Your brands"], ["deal", "Great deals"],
];

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const allVanished = (a: AddedOffer) => a.cards.every((c) => c.state === "vanished");
const anyVanished = (a: AddedOffer) => a.cards.some((c) => c.state === "vanished");

/**
 * Issuer offers (approved mockup, layout A, 2026-10-19), in five tabs:
 *
 * - Added (the default): what runs added for you, every issuer together,
 *   pinned ones on top. Pin uses the tracked offer's own pin route.
 * - Needs you: what a person must add: worth-adding offers of an issuer that
 *   does not add for you, and what auto-add gave up on or lost.
 * - Next run: only while something qualifies; per issuer that adds for you,
 *   exactly what the next run presses and on which card (`next_run`, from the
 *   server's plan rule). Don't add is Not interested.
 * - All offers and Expiring soon: every offer not yet added; an issuer that
 *   adds for you offers Add on next run on the rest (`flagged`).
 *
 * Whether an issuer adds for you is `offers.add.enabled` in
 * browser_sources.yaml, not a control here.
 */
export function WorthAdding({ finished, soonDays, onWorthCount, embedded = false, merchantQuery, cardId = null, onCatalogKeys }: {
  embedded?: boolean;
  merchantQuery?: string;
  cardId?: number | null;
  onCatalogKeys?: (keys: string[]) => void;
  finished: number;
  soonDays: number;
  /** How many items Needs you holds, for a count outside this section. */
  onWorthCount?: (n: number) => void;
}) {
  const [rawData, setData] = useState<Recommended | null>(null);
  const revision = useOfferRevision();
  const mutations = useOfferMutations();
  const merchantMatches = (merchant: string) => !embedded || merchant.toLocaleLowerCase().includes((merchantQuery ?? "").trim().toLocaleLowerCase());
  const data = useMemo(() => !rawData || cardId == null ? rawData : ({ ...rawData, issuers: rawData.issuers.map(issuer => ({
    ...issuer, cards: issuer.cards.filter(card => card.card_id === cardId),
    offers: issuer.offers.flatMap(offer => {
      const cards = offer.cards.filter(card => card.card_id === cardId);
      if (cards.length === 0) return [];
      const dates = cards.flatMap(card => card.expires_on ? [card.expires_on] : []);
      // Match offers_capture._merged for the selected rows, not the issuer-wide
      // aggregate. Worth/matched remain server-owned recommendation decisions.
      return [{ ...offer, cards,
        expires_on: dates.sort()[0] ?? null,
        first_seen_on: cards.map(card => card.first_seen_on).sort()[0]!,
        dismissed: cards.some(card => card.dismissed),
        flagged: cards.some(card => card.flagged),
        // MAX_ADD_FAILURES = 2 in points_ingest.offer_catalog; the API contract
        // defines needs_look as a card failing activation twice.
        needs_look: cards.some(card => card.add_failures >= 2),
        unconfirmed: cards.some(card => card.add_state === "unconfirmed"),
      }];
    }),
    next_run: issuer.next_run?.filter(add => add.card_id === cardId),
    // Activity has card labels, not IDs; never guess identity from a name.
    added_last_run: [],
  })).filter(issuer => issuer.cards.length > 0) }), [rawData, cardId]);
  useEffect(() => { onCatalogKeys?.(rawData?.issuers.flatMap(issuer => issuer.offers.flatMap(offer => offer.cards.map(card => card.capture_key))) ?? []); }, [rawData, onCatalogKeys]);
  const [error, setError] = useState<string | null>(null);
  // A refused write, shown above the list rather than in place of it.
  const [actionError, setActionError] = useState<string | null>(null);
  const addDismissals = useAddDismissals(setActionError);
  const [tab, setTab] = useState<OfferTab>("added");
  const [filter, setFilter] = useState<WorthFilter>("all");
  const [localQuery, setQuery] = useState("");
  const query = merchantQuery ?? localQuery;
  // Dismissals made on this page, applied at once and saved in the background.
  const [local, setLocal] = useState<Map<string, boolean>>(new Map());
  const [undo, setUndo] = useState<RecommendedOffer | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  // Add on next run per offer, as last saved here.
  const [flagLocal, setFlagLocal] = useState<Map<string, boolean>>(new Map());
  // Pin per added offer (by its change key), as last saved here.
  const [pinLocal, setPinLocal] = useState<Map<string, boolean>>(new Map());
  // The offer whose Add on next run is being confirmed, and its teach box.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [teach, setTeach] = useState(false);
  // Offers whose merchant was made one of your brands on this page.
  const [taught, setTaught] = useState<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // One write at a time per offer, so a quick Undo reaches the server after the hide.
  const writes = useRef<Map<string, Promise<void>>>(new Map());
  const pendingWrites = useRef(0);

  useEffect(() => {
    let active = true;
    fetchRecommended().then(
      (d) => {
        if (!active || pendingWrites.current > 0) return;
        setData(d); setLocal(new Map()); setFlagLocal(new Map()); setPinLocal(new Map()); setError(null);
      },
      (e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : "Could not load offers worth adding");
      },
    );
    return () => { active = false; };
  }, [finished, revision]);

  useEffect(() => () => { clearTimeout(timer.current); }, []);

  const isDismissed = (o: RecommendedOffer) => o.cards.some(card => local.get(card.capture_key) ?? card.dismissed);
  const isFlagged = (o: RecommendedOffer) => o.cards.some(card => flagLocal.get(card.capture_key) ?? card.flagged) || (!o.cards.some(card => flagLocal.has(card.capture_key)) && o.flagged);
  const isPinned = (a: AddedOffer) => pinLocal.get(a.key) ?? Boolean(a.pinned);
  // Switched on in browser_sources.yaml (`offers.add.enabled`), not here.
  const autoOn = (issuer: RecommendedIssuer) => issuer.auto_add.enabled;
  // What an issuer with adding on is asked to add.
  const adds = (o: RecommendedOffer) => (o.worth || isFlagged(o)) && !isDismissed(o);
  // Yours to add: not worth adding by a run, or one a card gave up on.
  const byHand = (issuer: RecommendedIssuer, o: RecommendedOffer) =>
    autoOn(issuer) ? adds(o) && o.needs_look : o.worth && !isDismissed(o);
  // A pinned add stays on Added, so it is not also a Needs you row.
  const vanishedOf = (issuer: RecommendedIssuer) => issuer.added_last_run.filter((a) =>
    merchantMatches(a.merchant) && !isPinned(a) && anyVanished(a) && !a.dismissed && !addDismissals.isHidden(a.key));
  // The offers the next run presses: its plan names one of their cards.
  const planned = (issuer: RecommendedIssuer) => {
    const next = issuer.next_run ?? [];
    return autoOn(issuer) && next.length > 0
      ? issuer.offers.filter((o) => merchantMatches(o.merchant) && adds(o) && addsFor(o, next).length > 0)
      : [];
  };

  const needsCount = data?.issuers.reduce((n, i) =>
    n + i.offers.filter((o) => merchantMatches(o.merchant) && byHand(i, o)).length + vanishedOf(i).length, 0) ?? null;
  useEffect(() => {
    if (needsCount !== null) onWorthCount?.(needsCount);
  }, [needsCount, onWorthCount]);

  if (error) {
    return <p role="alert" className="text-sm text-red-300">{error}</p>;
  }
  if (!data || data.issuers.length === 0) return null;

  // A sibling refresh cannot erase newer local intent while writes are pending.
  const settled = () => {
    pendingWrites.current -= 1;
    if (pendingWrites.current === 0) invalidateOffers();
  };
  const everyCard = async (jobs: Promise<void>[]) => {
    const results = await Promise.allSettled(jobs);
    const failed = results.find(result => result.status === "rejected");
    if (failed?.status === "rejected") throw failed.reason;
  };

  // Queue by capture identity so filtered and grouped operations cannot race.
  const writeCards = (offer: RecommendedOffer, operation: (key: string) => Promise<void>) =>
    everyCard(offer.cards.map(card => {
      const key = card.capture_key;
      const write = (writes.current.get(key) ?? Promise.resolve()).then(() => operation(key));
      writes.current.set(key, write.catch(() => undefined));
      return write;
    }));

  // Add on next run, or its Undo: every card's row, so whichever card the plan
  // reads it on asks for it. The brand is taught only once the flags are saved.
  const flag = (offer: RecommendedOffer, flagged: boolean, teachMerchant = false) => {
    const key = offerKey(offer);
    setFlagLocal(m => { const next = new Map(m); offer.cards.forEach(card => next.set(card.capture_key, flagged)); return next; });
    setConfirming(null);
    setActionError(null);
    if (!flagged) setTaught((s) => { const next = new Set(s); next.delete(key); return next; });
    pendingWrites.current += 1;
    const write = writeCards(offer, captureKey => setFlagged(captureKey, flagged))
      .then(
        async () => {
          if (teachMerchant) {
            try {
              await addInterest(offer.merchant);
              setTaught((s) => new Set(s).add(key));
            } catch (e: unknown) {
              // The flags were saved; only the brand write failed.
              setActionError(errorText(e));
            }
          }
          // The finally reconciliation includes any brand write above.
        },
        (e: unknown) => {
          setActionError(errorText(e));
        },
      ).finally(settled);
    void write;
  };

  const dismiss = (offer: RecommendedOffer, dismissed: boolean) => {
    setLocal(m => { const next = new Map(m); offer.cards.forEach(card => next.set(card.capture_key, dismissed)); return next; });
    clearTimeout(timer.current);
    if (dismissed) {
      setUndo(offer);
      timer.current = setTimeout(() => { setUndo(null); }, UNDO_MS);
    } else {
      setUndo(null);
    }
    setActionError(null);
    // Every card it is on, so the offer stays put away whichever card a run reads.
    pendingWrites.current += 1;
    const write = writeCards(offer, captureKey => setDismissed(captureKey, dismissed))
      .catch((e: unknown) => {
        // Partial success is reconciled after all card writes settle.
        setActionError(errorText(e));
      }).finally(settled);
    void write;
  };

  // Pin or unpin every tracked offer the add became, one at a time: each write
  // republishes the podcast feed, and concurrent ones could land out of order.
  // Shared tracked-ID locks ignore overlapping pin requests on any surface.
  // A refusal may leave some cards pinned, so server state is reloaded.
  const pin = (added: AddedOffer, pinned: boolean) => {
    const ids = added.offer_ids ?? [];
    if (ids.length === 0 || ids.some(mutations.isBusy)) return;
    setPinLocal(m => new Map(m).set(added.key, pinned));
    setActionError(null);
    pendingWrites.current += 1;
    void mutations.run(ids, () => ids.reduce<Promise<unknown>>(
      (previous, id) => previous.then(() => setOfferPinned(id, pinned)), Promise.resolve()))
      .catch((e: unknown) => { setActionError(errorText(e)); }).finally(settled);
  };

  const nextCount = data.issuers.reduce((n, i) => n + planned(i).length, 0);
  // Next run is drawn only while something qualifies; otherwise Added stands in.
  const shown: OfferTab = embedded ? "all" : tab === "next" && nextCount === 0 ? "added" : tab;
  const added = data.issuers.flatMap((i) => i.added_last_run).filter((a) =>
    merchantMatches(a.merchant) && (isPinned(a) || (!a.dismissed && !addDismissals.isHidden(a.key) && !allVanished(a))));

  const needle = query.trim().toLocaleLowerCase();
  const listed = (o: RecommendedOffer, t: "all" | "soon") =>
    t === "soon" ? endsSoon(o, data.today, soonDays) : o.merchant.toLocaleLowerCase().includes(needle);
  const listCount = (t: "all" | "soon") => data.issuers.reduce((n, i) =>
    n + i.offers.filter((o) => t === "all" || listed(o, t)).length, 0);
  const shownCount = shown === "all" || shown === "soon"
    ? data.issuers.reduce((n, i) => n + i.offers.filter((o) => listed(o, shown)).length, 0) : 0;
  const byHandShown = data.issuers.reduce((n, i) =>
    n + i.offers.filter((o) => byHand(i, o) && matchesFilter(o, filter)).length, 0);
  const hidden = data.issuers.flatMap((issuer) =>
    issuer.offers.filter((o) => (o.worth || (autoOn(issuer) && isFlagged(o))) && isDismissed(o))
      .map((offer) => ({ issuer, offer })));

  const tabs: { key: OfferTab; label: string; count: number; hot?: boolean }[] = [
    { key: "added", label: "Added", count: added.length },
    { key: "needs", label: "Needs you", count: needsCount ?? 0, hot: true },
    ...(nextCount > 0 ? [{ key: "next" as const, label: "Next run", count: nextCount }] : []),
    { key: "all", label: "All offers", count: listCount("all") },
    { key: "soon", label: "Expiring soon", count: listCount("soon") },
  ];
  const subtitle = shown === "added" ? `${added.length} added for you, from the last runs`
    : shown === "needs" ? `${byHandShown} ${byHandShown === 1 ? "offer" : "offers"} to add by hand, from the last run`
      : shown === "next" ? `${nextCount} ${nextCount === 1 ? "offer" : "offers"} on the next run`
        : `${shownCount} ${shownCount === 1 ? "offer" : "offers"} ${
          shown === "soon" ? `ending within ${soonDays} days` : "across your cards"}, from the last run`;

  return (
    <section aria-label="Issuer offers" className="space-y-2.5">
      {addDismissals.toast}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <SectionTitle>{embedded ? "Available to activate" : "Issuer offers"}</SectionTitle>
        <span className="text-[12.5px] text-neutral-500">{subtitle}</span>
        {shown === "needs" && (
          <div role="group" aria-label="Filter offers worth adding" className="flex flex-wrap gap-1.5 sm:ml-auto">
            {FILTERS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => { setFilter(key); }}
                className={`rounded-full border px-2.5 py-0.5 text-xs ${
                  filter === key
                    ? "border-accent bg-accent/10 text-neutral-100"
                    : "border-neutral-700 text-neutral-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!embedded && <div role="tablist" aria-label="Which offers" className="flex gap-1 overflow-x-auto border-b border-neutral-800">
        {tabs.map(({ key, label, count, hot }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={shown === key}
            onClick={() => { setTab(key); }}
            className={`whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[13.5px] ${
              shown === key ? "border-accent font-semibold text-neutral-100" : "border-transparent text-neutral-400"
            }`}
          >
            {label}
            <span
              className={`ml-1 font-mono text-[11.5px] ${
                hot && count > 0 ? "rounded-full bg-accent px-1.5 font-semibold text-neutral-950" : "text-neutral-500"
              }`}
            >
              {count}
            </span>
          </button>
        ))}
      </div>}

      {shown === "all" && !embedded && (
        <input
          type="search"
          aria-label="Search merchants"
          placeholder="Search merchants"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          className="w-full rounded-lg border border-neutral-800 bg-neutral-900 px-2.5 py-1.5 text-[13px]"
        />
      )}

      {embedded && nextCount > 0 && <details className="rounded-xl border border-neutral-800 p-3">
        <summary className="cursor-pointer text-sm">Next refresh: {nextCount} to activate</summary>
        <p className="my-2 text-xs text-neutral-500">Queued is not activated. The next issuer read must confirm activation.</p>
        {data.issuers.map(issuer => planned(issuer).length > 0 && <NextRunList key={issuer.source} issuer={issuer} offers={planned(issuer)} today={data.today} soonDays={soonDays} isFlagged={isFlagged} onDontAdd={offer => dismiss(offer, true)} />)}
      </details>}
      {embedded && data.issuers.some(issuer => vanishedOf(issuer).length > 0) && <section aria-label="Activation needs confirmation">
        <SectionTitle>Needs confirmation</SectionTitle>
        <p className="my-2 text-xs text-neutral-500">The issuer no longer lists these attempted activations. They are not confirmed ready to use.</p>
        {data.issuers.map(issuer => <ul key={issuer.source}>{vanishedOf(issuer).map(added => <VanishedAdd key={added.key} added={added} cards={issuer.cards} onDismiss={() => addDismissals.hide([added.key])} />)}</ul>)}
      </section>}
      {actionError && <p role="alert" className="text-sm text-red-300">{actionError}</p>}

      {embedded && cardId != null && <p className="text-xs text-neutral-500">Recent activation activity is available with the All cards filter; these records do not identify cards reliably.</p>}
      {embedded && cardId == null && added.length > 0 && <details className="rounded-xl border border-neutral-800 p-3">
        <summary className="cursor-pointer text-sm">Recent activation activity · {added.length}</summary>
        <p className="my-2 text-xs text-neutral-500">Activation attempts, not your ready-to-use list. Check each card's confirmation status.</p>
        <AddedForYou added={added} today={data.today} soonDays={soonDays} isPinned={isPinned} onPin={pin}
          isBusy={added => (added.offer_ids ?? []).some(mutations.isBusy)} onDismiss={list => addDismissals.hide(list.map(a => a.key))} />
      </details>}

      {shown === "added" && (
        <AddedForYou
          added={added}
          isBusy={added => (added.offer_ids ?? []).some(mutations.isBusy)}
          today={data.today}
          soonDays={soonDays}
          isPinned={isPinned}
          onPin={pin}
          onDismiss={(list) => { addDismissals.hide(list.map((a) => a.key)); }}
        />
      )}

      {shown === "needs" && !data.interests_configured && (
        <p className="text-xs text-amber-400">
          The interests list is empty, so nothing is picked out. Add brands to offer_interests.yaml.
        </p>
      )}

      {shown === "needs" && needsCount === 0 && data.issuers.every(autoOn) && (
        <p className="rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-3 text-[13px] text-neutral-400">
          Nothing needs you. Runs add these issuers' offers on their own.
        </p>
      )}

      {shown === "needs" && data.issuers.map((issuer) => {
        const offers = issuer.offers.filter((o) => byHand(issuer, o) && matchesFilter(o, filter));
        const vanished = vanishedOf(issuer);
        // An issuer that adds for you appears only when it left something to you.
        if (offers.length === 0 && (autoOn(issuer) ? vanished.length === 0 : filter !== "all")) return null;
        const read = issuer.cards.some((c) => c.last_read_on);
        return (
          <IssuerBox key={issuer.source} issuer={issuer} today={data.today}>
            {offers.length === 0 && vanished.length === 0 ? (
              <p className="px-3 py-3 text-[13px] text-neutral-400">
                {read
                  ? `Nothing from your brands or great deals among ${issuer.offers.length} ${issuer.offers.length === 1 ? "offer" : "offers"} read.`
                  : "Not read yet. Run it from Refresh."}
              </p>
            ) : (
              <ul className="divide-y divide-neutral-800">
                {offers.map((offer) => {
                  const url = issuer.cards.find((c) => offer.cards.some((x) => x.card_id === c.card_id))?.url;
                  return (
                    <OfferLine
                      key={offerKey(offer)}
                      offer={offer}
                      cards={issuer.cards}
                      today={data.today}
                      soonDays={soonDays}
                      notes={offer.needs_look
                        ? [{ text: "did not take twice; add it on the issuer's site", tone: "text-amber-400" }]
                        : []}
                      action={
                        <span className="flex flex-wrap items-center gap-1.5">
                          {url && (
                            <a href={url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
                              Open {issuer.issuer}
                            </a>
                          )}
                          <button type="button" onClick={() => { dismiss(offer, true); }} className="btn btn-ghost btn-sm">
                            Not interested
                          </button>
                        </span>
                      }
                    />
                  );
                })}
                {vanished.map((a) => (
                  <VanishedAdd
                    key={a.key}
                    added={a}
                    cards={issuer.cards}
                    onDismiss={() => { addDismissals.hide([a.key]); }}
                  />
                ))}
              </ul>
            )}
          </IssuerBox>
        );
      })}

      {shown === "next" && data.issuers.map((issuer) => {
        const offers = planned(issuer);
        return offers.length === 0 ? null : (
          <NextRunList
            key={issuer.source}
            issuer={issuer}
            offers={offers}
            today={data.today}
            soonDays={soonDays}
            isFlagged={isFlagged}
            onDontAdd={(offer) => { dismiss(offer, true); }}
          />
        );
      })}

      {(shown === "all" || shown === "soon") && data.issuers.map((issuer) => {
        const offers = issuer.offers.filter((o) => listed(o, shown));
        if (offers.length === 0) return null;
        const auto = autoOn(issuer);
        return (
          <IssuerBox key={issuer.source} issuer={issuer} today={data.today}>
            <ul className="divide-y divide-neutral-800">
              {offers.map((offer) => {
                const key = offerKey(offer);
                // Ask the next run to add one it would not.
                const askable = auto && !offer.worth && !isDismissed(offer);
                const flagged = askable && isFlagged(offer);
                return (
                  <OfferLine
                    key={key}
                    offer={offer}
                    cards={issuer.cards}
                    today={data.today}
                    soonDays={soonDays}
                    action={!auto ? (
                      <a href={issuer.cards.find(card => offer.cards.some(value => value.card_id === card.card_id))?.url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">Open {issuer.issuer} to activate</a>
                    ) : flagged ? (
                      <span className="flex items-center gap-2">
                        {offer.needs_look ? (
                          <span className="text-[12.5px] text-amber-400">
                            did not take twice; add it on the issuer's site
                          </span>
                        ) : (
                          addsFor(offer, issuer.next_run ?? []).length > 0 ? (
                          <span className="text-[12.5px] text-green-400">Adds on the next run</span>
                        ) : (
                          <span className="text-[12.5px] text-neutral-400">Add requested; not scheduled on this card</span>
                        )
                        )}
                        <button type="button" onClick={() => { flag(offer, false); }} className="btn btn-ghost btn-sm">
                          Undo
                        </button>
                      </span>
                    ) : askable && confirming !== key ? (
                      <button
                        type="button"
                        onClick={() => { setConfirming(key); setTeach(false); }}
                        className="btn btn-ghost btn-sm"
                      >
                        Add on next run
                      </button>
                    ) : undefined}
                    footer={<>{askable && !flagged && confirming === key ? (
                      <ConfirmAdd
                        merchant={offer.merchant}
                        teach={teach}
                        onTeach={setTeach}
                        onCancel={() => { setConfirming(null); }}
                        onConfirm={() => { flag(offer, true, teach); }}
                      />
                    ) : flagged && taught.has(key) ? (
                      <p className="text-xs text-neutral-500">
                        {offer.merchant} is now one of your brands. Remove it under Settings.
                      </p>
                    ) : undefined}
                    {embedded && <span className="flex flex-wrap items-center gap-2">
                      {offer.needs_look && <a className="text-xs text-amber-400 underline" href={issuer.cards.find(card => offer.cards.some(value => value.card_id === card.card_id))?.url} target="_blank" rel="noreferrer">Activation needs your help on the issuer site.</a>}
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => dismiss(offer, !isDismissed(offer))}>{isDismissed(offer) ? "Show again" : "Not interested"}</button>
                    </span>}
                    </>}
                  />
                );
              })}
            </ul>
          </IssuerBox>
        );
      })}

      {(shown === "all" || shown === "soon") && shownCount === 0 && (
        <p className="text-[13px] text-neutral-400">
          {shown === "soon" ? `Nothing you have not added ends within ${soonDays} days.` : "No offer matches that search."}
        </p>
      )}

      {(shown === "needs" || shown === "next") && hidden.length > 0 && (
        <div>
          <Fold
            open={showDismissed}
            onClick={() => { setShowDismissed(!showDismissed); }}
            label={`Not interested · ${hidden.length}`}
          />
          {showDismissed && (
            <ul className="mt-1.5 flex flex-col gap-1.5 text-[12.5px] text-neutral-400">
              {hidden.map(({ issuer, offer }) => (
                <li key={offerKey(offer)} className="flex flex-wrap items-center gap-2">
                  <span>{offer.merchant} · {issuer.issuer}</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { dismiss(offer, false); }}>
                    Show again
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {undo && (
        <div
          role="status"
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-2.5 rounded-xl border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm"
        >
          <span>Hid {undo.merchant}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { dismiss(undo, false); }}>
            Undo
          </button>
        </div>
      )}
    </section>
  );
}

/** One issuer's box: its name and how fresh each card's read is. */
function IssuerBox({ issuer, today, children }: {
  issuer: RecommendedIssuer;
  today: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 border-b border-neutral-800 px-3 py-2.5">
        <span className="text-[14px] font-semibold">{issuer.issuer}</span>
        <span className="text-[11.5px] text-neutral-500">
          {readCoverage(issuer.cards, today).map((fresh, i) => (
            <span key={fresh.text}>
              {i > 0 && " · "}
              <span className={fresh.stale ? "text-amber-400" : ""}>{fresh.text}</span>
            </span>
          ))}
        </span>
      </div>
      {children}
    </div>
  );
}

function ConfirmAdd({ merchant, teach, onTeach, onCancel, onConfirm }: {
  merchant: string;
  teach: boolean;
  onTeach: (teach: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-x-3 gap-y-2 rounded-lg bg-neutral-800/60 px-2.5 py-2 sm:flex-row sm:flex-wrap sm:items-center">
      <label className="flex min-w-0 flex-1 items-start gap-2 text-[12.5px] text-neutral-300">
        <input
          type="checkbox"
          checked={teach}
          onChange={(e) => { onTeach(e.target.checked); }}
          className="mt-0.5 h-4 w-4 shrink-0 accent-sky-400"
        />
        Also treat future {merchant} offers as worth adding, on every issuer
      </label>
      <span className="flex gap-1.5">
        <button type="button" onClick={onCancel} className="btn btn-ghost btn-sm">Cancel</button>
        <button type="button" onClick={onConfirm} className="btn btn-primary btn-sm">Add on next run</button>
      </span>
    </div>
  );
}
